import { CoachingFormData, CHANGE_OPTIONS, READINESS_OPTIONS } from './types';

interface Props {
  data: CoachingFormData;
  onChange: (updates: Partial<CoachingFormData>) => void;
  errors: Record<string, string>;
}

function CheckOption({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`text-left px-4 py-3 rounded-lg border text-sm transition-all duration-200 cursor-pointer ${
        checked
          ? 'border-[#1E2D5E] bg-[#1E2D5E]/5 text-[#1E2D5E] font-medium'
          : 'border-[#C9A96E]/30 bg-white text-[#1E2D5E]/70 hover:border-[#C9A96E]'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
            checked ? 'bg-[#1E2D5E] border-[#1E2D5E]' : 'border-[#C9A96E]/40'
          }`}
        >
          {checked && <i className="ri-check-line text-white text-xs" />}
        </div>
        <span>{label}</span>
      </div>
    </button>
  );
}

export default function IntakeStep3Goals({ data, onChange, errors }: Props) {
  const toggleChange = (value: string) => {
    const current = data.desired_changes;
    onChange({
      desired_changes: current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h3
          className="text-xl md:text-2xl font-light text-[#1E2D5E] mb-1"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Goals, Readiness &amp; Fit
        </h3>
        <p className="text-sm text-[#1E2D5E]/60 leading-relaxed">
          This helps Lauren understand what you&apos;re working toward and how she can best support you.
        </p>
      </div>

      {/* Desired Changes */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-1.5">
          What would you most like to change or improve?{' '}
          <span className="text-[#1E2D5E]/40 font-normal">(select all that apply)</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          {CHANGE_OPTIONS.map((opt) => (
            <CheckOption
              key={opt}
              label={opt}
              checked={data.desired_changes.includes(opt)}
              onToggle={() => toggleChange(opt)}
            />
          ))}
        </div>
      </div>

      {/* Coaching Hope */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-1.5">
          If this coaching worked exactly how you hoped, what would feel different in your life?{' '}
          <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-[#1E2D5E]/45 mb-2">
          Dream a little — what does a &ldquo;good day&rdquo; look like for you?
        </p>
        <textarea
          name="coaching_hope"
          value={data.coaching_hope}
          onChange={(e) => onChange({ coaching_hope: e.target.value })}
          placeholder="I would feel calmer, more focused, and actually finish what I start..."
          rows={4}
          maxLength={500}
          className={`w-full px-4 py-3 rounded-lg border text-[#1E2D5E] text-sm bg-white focus:outline-none transition-colors resize-none ${
            errors.coaching_hope ? 'border-red-400' : 'border-[#C9A96E]/30 focus:border-[#C9A96E]'
          }`}
        />
        <div className="flex justify-between mt-1">
          {errors.coaching_hope && <p className="text-xs text-red-400">{errors.coaching_hope}</p>}
          <p className="text-xs text-[#1E2D5E]/35 ml-auto">{data.coaching_hope.length}/500</p>
        </div>
      </div>

      {/* Readiness */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-3">
          Are you ready to actively implement changes and strategies?{' '}
          <span className="text-red-400">*</span>
        </label>
        <div className="space-y-2">
          {READINESS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ readiness: opt.value })}
              className={`w-full text-left px-4 py-3.5 rounded-lg border text-sm transition-all cursor-pointer ${
                data.readiness === opt.value
                  ? 'border-[#1E2D5E] bg-[#1E2D5E]/5 text-[#1E2D5E] font-medium'
                  : 'border-[#C9A96E]/30 bg-white text-[#1E2D5E]/70 hover:border-[#C9A96E]'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    data.readiness === opt.value
                      ? 'border-[#1E2D5E]'
                      : 'border-[#C9A96E]/40'
                  }`}
                >
                  {data.readiness === opt.value && (
                    <div className="w-2 h-2 rounded-full bg-[#1E2D5E]" />
                  )}
                </div>
                <span>{opt.label}</span>
              </div>
            </button>
          ))}
        </div>
        {errors.readiness && <p className="text-xs text-red-400 mt-1">{errors.readiness}</p>}
      </div>

      {/* Obstacles */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-1.5">
          What has stopped you from making these changes so far?
        </label>
        <p className="text-xs text-[#1E2D5E]/45 mb-2">
          Be honest — there&apos;s no judgment here. This helps Lauren understand your starting point.
        </p>
        <textarea
          name="obstacles"
          value={data.obstacles}
          onChange={(e) => onChange({ obstacles: e.target.value })}
          placeholder="I've tried before but..."
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 rounded-lg border border-[#C9A96E]/30 text-[#1E2D5E] text-sm bg-white focus:outline-none focus:border-[#C9A96E] transition-colors resize-none"
        />
        <p className="text-xs text-[#1E2D5E]/35 text-right mt-1">{data.obstacles.length}/500</p>
      </div>

      {/* Coaching Fit Notice */}
      <div className="bg-[#FDF8F0] border border-[#C9A96E]/25 rounded-xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <i className="ri-heart-line text-[#C9A96E] text-lg" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-[#1E2D5E] mb-1">A Note About Coaching</h4>
            <p className="text-xs text-[#1E2D5E]/65 leading-relaxed">
              ADHD coaching with Lauren is <strong>action-oriented</strong> — it&apos;s focused on
              building practical systems, routines, and strategies that work for your brain. Coaching is{' '}
              <strong>not therapy</strong> and is not a replacement for medical care or medication
              management. It&apos;s a powerful complement to treatment, not a substitute.
            </p>
          </div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer group">
          <div
            className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors ${
              data.fit_acknowledgment
                ? 'bg-[#1E2D5E] border-[#1E2D5E]'
                : 'border-[#C9A96E]/50 group-hover:border-[#C9A96E]'
            }`}
            onClick={() => onChange({ fit_acknowledgment: !data.fit_acknowledgment })}
          >
            {data.fit_acknowledgment && <i className="ri-check-line text-white text-xs" />}
          </div>
          <span className="text-xs text-[#1E2D5E]/75 leading-relaxed">
            I understand that coaching is focused on practical strategies and implementation, and is{' '}
            <strong>not</strong> a replacement for therapy or medical care.{' '}
            <span className="text-red-400">*</span>
          </span>
        </label>
        {errors.fit_acknowledgment && (
          <p className="text-xs text-red-400 mt-2">{errors.fit_acknowledgment}</p>
        )}
      </div>

      {/* Final Reflective Question */}
      <div>
        <label className="block text-sm font-semibold text-[#1E2D5E] mb-1.5">
          What made you decide to seek support right now?{' '}
          <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-[#1E2D5E]/45 mb-2">
          Something brought you here today — what was it?
        </p>
        <textarea
          name="seek_support_reason"
          value={data.seek_support_reason}
          onChange={(e) => onChange({ seek_support_reason: e.target.value })}
          placeholder="I finally decided to reach out because..."
          rows={4}
          maxLength={500}
          className={`w-full px-4 py-3 rounded-lg border text-[#1E2D5E] text-sm bg-white focus:outline-none transition-colors resize-none ${
            errors.seek_support_reason ? 'border-red-400' : 'border-[#C9A96E]/30 focus:border-[#C9A96E]'
          }`}
        />
        <div className="flex justify-between mt-1">
          {errors.seek_support_reason && (
            <p className="text-xs text-red-400">{errors.seek_support_reason}</p>
          )}
          <p className="text-xs text-[#1E2D5E]/35 ml-auto">{data.seek_support_reason.length}/500</p>
        </div>
      </div>
    </div>
  );
}
